import React, { useState, useEffect } from 'react';
import { 
  Server,
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  TrendingUp,
  Calendar,
  Settings,
  Users,
  Package,
  Truck,
  Play,
  Pause,
  RotateCcw,
  FileText,
  Download,
  Monitor
} from 'lucide-react';

const SystemAdmin = () => {
  const [systemStatus, setSystemStatus] = useState(null);
  const [cronLogs, setCronLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch system status
  const fetchSystemStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Note: This endpoint needs to be updated to use a different API
      // For now, setting mock data
      setSystemStatus({
        system_health: {
          status: 'healthy',
          last_check: new Date().toISOString()
        },
        event_scheduler_enabled: true,
        upcoming_deliveries: [],
        recent_cron_logs: []
      });
    } catch (error) {
      console.error('Error fetching system status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSystemStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const StatusIndicator = ({ status, label }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'healthy':
        case 'ON':
        case true:
          return 'text-green-600 bg-green-100';
        case 'warning':
          return 'text-yellow-600 bg-yellow-100';
        case 'error':
        case 'OFF':
        case false:
          return 'text-red-600 bg-red-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'healthy':
        case 'ON':
        case true:
          return CheckCircle;
        case 'warning':
          return AlertTriangle;
        case 'error':
        case 'OFF':
        case false:
          return XCircle;
        default:
          return Monitor;
      }
    };

    const StatusIcon = getStatusIcon(status);

    return (
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-full ${getStatusColor(status)}`}>
        <StatusIcon className="h-4 w-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
    );
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      {/* System Health Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              <p className="text-sm text-gray-600">Overall system status</p>
            </div>
            <Server className="h-8 w-8 text-blue-600" />
          </div>
          <div className="mt-4">
            <StatusIndicator 
              status={systemStatus?.system_health?.status}
              label={systemStatus?.system_health?.status || 'Unknown'}
            />
            <p className="text-xs text-gray-500 mt-2">
              Last check: {systemStatus?.system_health?.last_check ? 
                new Date(systemStatus.system_health.last_check).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Event Scheduler</h3>
              <p className="text-sm text-gray-600">MySQL cron jobs</p>
            </div>
            <Clock className="h-8 w-8 text-green-600" />
          </div>
          <div className="mt-4">
            <StatusIndicator 
              status={systemStatus?.event_scheduler_enabled}
              label={systemStatus?.event_scheduler_enabled ? 'Active' : 'Disabled'}
            />
            <p className="text-xs text-gray-500 mt-2">
              Manages daily order generation
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Database</h3>
              <p className="text-sm text-gray-600">Connection status</p>
            </div>
            <Database className="h-8 w-8 text-purple-600" />
          </div>
          <div className="mt-4">
            <StatusIndicator 
              status="healthy"
              label="Connected"
            />
            <p className="text-xs text-gray-500 mt-2">
              Remote MySQL database
            </p>
          </div>
        </div>
      </div>

      {/* Upcoming Deliveries */}
      {systemStatus?.upcoming_deliveries && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Deliveries</h3>
              <p className="text-sm text-gray-600">Next 7 days delivery schedule</p>
            </div>
            <Truck className="h-6 w-6 text-blue-600" />
          </div>
          
          {systemStatus.upcoming_deliveries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
              {systemStatus.upcoming_deliveries.map((delivery, index) => (
                <div key={index} className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">
                    {new Date(delivery.delivery_date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="text-lg font-bold text-blue-800">{delivery.scheduled_orders}</p>
                  <p className="text-xs text-blue-600">orders</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No upcoming deliveries scheduled</p>
            </div>
          )}
        </div>
      )}


    </div>
  );

  const CronLogsTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cron Job Logs</h3>
              <p className="text-sm text-gray-600">Recent automated task executions</p>
            </div>
            <button
              onClick={fetchSystemStatus}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        <div className="p-6">
          {systemStatus?.recent_cron_logs && systemStatus.recent_cron_logs.length > 0 ? (
            <div className="space-y-4">
              {systemStatus.recent_cron_logs.map((log, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{log.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Log Type: {log.log_type}</p>
                    {log.details && (
                      <div className="mt-2">
                        <details className="cursor-pointer">
                          <summary className="text-xs text-blue-600 hover:text-blue-800">
                            View Details
                          </summary>
                          <pre className="text-xs text-gray-600 mt-1 p-2 bg-white rounded border overflow-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No cron job logs available</p>
              <p className="text-sm mt-2">Logs will appear here when automated tasks run</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const ConfigurationTab = () => (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Configuration</h3>
        
        <div className="space-y-6">
          <div className="border-b border-gray-200 pb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Event Scheduler Settings</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">Daily Order Generation</p>
                  <p className="text-xs text-gray-500">Runs daily at 1:00 AM to create subscription orders</p>
                </div>
                <StatusIndicator 
                  status={systemStatus?.event_scheduler_enabled}
                  label={systemStatus?.event_scheduler_enabled ? 'Active' : 'Disabled'}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">Delivery Notifications</p>
                  <p className="text-xs text-gray-500">Runs daily at 6:00 PM to prepare next day deliveries</p>
                </div>
                <StatusIndicator 
                  status={systemStatus?.event_scheduler_enabled}
                  label={systemStatus?.event_scheduler_enabled ? 'Active' : 'Disabled'}
                />
              </div>
            </div>
          </div>

          <div className="border-b border-gray-200 pb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Database Configuration</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <p>Host: s3738.bom1.stableserver.net</p>
              <p>Database: farmindi_saas_admin</p>
              <p>Port: 3306</p>
              <p>Connection Pool: Active</p>
            </div>
          </div>

          <div className="border-b border-gray-200 pb-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Admin Permissions</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Developer Super Admin Access</span>
                <StatusIndicator status="healthy" label="Enabled" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Delivery Order Management</span>
                <StatusIndicator status="healthy" label="Enabled" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Subscription Management</span>
                <StatusIndicator status="healthy" label="Enabled" />
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">System Actions</h4>
            <div className="flex space-x-3">
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <Download className="h-4 w-4 mr-2" />
                Export Logs
              </button>
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear Cache
              </button>
              <button className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100">
                <RefreshCw className="h-4 w-4 mr-2" />
                Restart Services
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading && !systemStatus) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading system status...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Administration</h1>
          <p className="text-gray-600">Monitor system health, cron jobs, and delivery operations</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSystemStatus}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button 
                onClick={fetchSystemStatus}
                className="mt-2 text-sm text-red-800 underline hover:text-red-600"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Activity className="h-4 w-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('cronlogs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'cronlogs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Cron Logs
          </button>
          <button
            onClick={() => setActiveTab('configuration')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'configuration'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings className="h-4 w-4 inline mr-2" />
            Configuration
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'cronlogs' && <CronLogsTab />}
        {activeTab === 'configuration' && <ConfigurationTab />}
      </div>
    </div>
  );
};

export default SystemAdmin;